import React, { useState, useEffect } from 'react';
import { Wrench, AlertCircle, Check, X, MapPin, Calendar, User, Edit2, Trash2, Search, Loader2, ChevronRight, Camera, ClipboardList } from 'lucide-react';
import { CustomSelect } from '../common/CustomSelect';

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
}

interface EquipmentIssue {
  issue_id: number;
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
  issue_description: string;
  severity: 'minor' | 'moderate' | 'critical';
  reported_date: string;
  is_fixed: boolean;
  fixed_date: string | null;
  fixed_by_employee_id: number | null;
  fix_description: string | null;
  cost_to_fix: number | null;
  downtime_hours: number | null;
  issue_photos: string | null;
  fix_photos: string | null;
  parts_status: 'none' | 'need_parts' | 'parts_ordered' | 'waiting_on_parts' | 'parts_received' | null;
  job_id: number | null;
  job_name: string | null;
  bradanna_job_number: string | null;
  reporter_first_name: string;
  reporter_last_name: string;
  fixer_first_name: string | null;
  fixer_last_name: string | null;
}

// General (non-equipment) shop tasks from the kiosk board's Shop To-Do list.
// Shown in their own section under the equipment issues — kept out of the
// issues array so counts (page badge, dashboard KPI) stay issues-only.
interface ShopTodo {
  todo_id: number;
  title: string;
  priority: 'low' | 'normal' | 'high';
  created_at: string;
  created_by: string | null;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

const translations = {
  en: {
    title: 'Equipment Issues',
    all: 'All',
    open: 'Open',
    resolved: 'Resolved',
    severity: 'Severity',
    equipment: 'Equipment',
    reportedBy: 'Reported by',
    location: 'Location',
    reportedOn: 'Reported',
    resolvedOn: 'Resolved',
    markResolved: 'Mark as Fixed',
    viewDetails: 'View Details',
    noIssues: 'No issues found',
    resolveIssue: 'Resolve Issue',
    fixDescription: 'Fix Description',
    costToFix: 'Cost to Fix ($)',
    downtimeHours: 'Downtime (hours)',
    cancel: 'Cancel',
    submit: 'Submit',
    optional: 'Optional',
    issueResolved: 'Issue marked as resolved',
    critical: 'Critical',
    moderate: 'Moderate',
    minor: 'Minor',
    partsStatus: 'Parts Status',
    partsNone: 'No Parts Needed',
    partsNeedParts: 'Need Parts',
    partsOrdered: 'Parts Ordered',
    partsWaiting: 'Waiting on Parts',
    partsReceived: 'Parts Received',
    updatePartsStatus: 'Update Parts Status',
    viewPhotos: 'View Photos',
    photos: 'Photos',
    description: 'Description',
    editIssue: 'Edit Issue',
    deleteIssue: 'Delete Issue',
    confirmDelete: 'Are you sure you want to delete this issue?',
    deleteWarning: 'This action cannot be undone.',
    save: 'Save Changes',
    changeMachine: 'Change Equipment',
    searchEquipment: 'Search equipment...',
    fixPhotos: 'Fix Photos',
    addPhotos: 'Add Photos',
    shopTodoTitle: 'Shop To-Do',
    shopTodoSubtitle: 'General shop tasks from the kiosk board',
    markDone: 'Done',
    addedBy: 'Added by',
    confirmMarkDone: 'Mark this task as done?'
  },
  es: {
    title: 'Problemas de Equipo',
    all: 'Todos',
    open: 'Abiertos',
    resolved: 'Resueltos',
    severity: 'Gravedad',
    equipment: 'Equipo',
    reportedBy: 'Reportado por',
    location: 'Ubicación',
    reportedOn: 'Reportado',
    resolvedOn: 'Resuelto',
    markResolved: 'Marcar como Arreglado',
    viewDetails: 'Ver Detalles',
    noIssues: 'No se encontraron problemas',
    resolveIssue: 'Resolver Problema',
    fixDescription: 'Descripción de la Reparación',
    costToFix: 'Costo de Reparación ($)',
    downtimeHours: 'Tiempo Inactivo (horas)',
    cancel: 'Cancelar',
    submit: 'Enviar',
    optional: 'Opcional',
    issueResolved: 'Problema marcado como resuelto',
    critical: 'Crítico',
    moderate: 'Moderado',
    minor: 'Menor',
    partsStatus: 'Estado de Partes',
    partsNone: 'No se Necesitan Partes',
    partsNeedParts: 'Necesita Partes',
    partsOrdered: 'Partes Ordenadas',
    partsWaiting: 'Esperando Partes',
    partsReceived: 'Partes Recibidas',
    updatePartsStatus: 'Actualizar Estado de Partes',
    viewPhotos: 'Ver Fotos',
    photos: 'Fotos',
    description: 'Descripción',
    editIssue: 'Editar Problema',
    deleteIssue: 'Eliminar Problema',
    confirmDelete: '¿Está seguro de que desea eliminar este problema?',
    deleteWarning: 'Esta acción no se puede deshacer.',
    save: 'Guardar Cambios',
    changeMachine: 'Cambiar Equipo',
    searchEquipment: 'Buscar equipo...',
    fixPhotos: 'Fotos de la Reparación',
    addPhotos: 'Agregar Fotos',
    shopTodoTitle: 'Tareas del Taller',
    shopTodoSubtitle: 'Tareas generales del taller del tablero del kiosco',
    markDone: 'Hecho',
    addedBy: 'Agregado por',
    confirmMarkDone: '¿Marcar esta tarea como hecha?'
  }
};

export const MechanicsIssues: React.FC = () => {
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const [issues, setIssues] = useState<EquipmentIssue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<EquipmentIssue[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'fixed'>('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Equipment list for changing machine
  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);

  // Kiosk-board Shop To-Do items (open only) shown under the issues list
  const [shopTodos, setShopTodos] = useState<ShopTodo[]>([]);
  const [markingDoneId, setMarkingDoneId] = useState<number | null>(null);

  // Resolution modal state
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<EquipmentIssue | null>(null);
  const [fixDescription, setFixDescription] = useState('');
  const [costToFix, setCostToFix] = useState('');
  const [downtimeHours, setDowntimeHours] = useState('');
  const [fixPhotoFiles, setFixPhotoFiles] = useState<File[]>([]);
  const [fixPhotoPreviews, setFixPhotoPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detail view modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailIssue, setDetailIssue] = useState<EquipmentIssue | null>(null);
  const [partsStatus, setPartsStatus] = useState<string>('none');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editSeverity, setEditSeverity] = useState<string>('');
  const [editEquipmentId, setEditEquipmentId] = useState<number | null>(null);
  const [editPartsStatus, setEditPartsStatus] = useState<string>('none');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIssue, setDeleteIssue] = useState<EquipmentIssue | null>(null);

  const t = translations[language];

  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '100px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '20px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px'
    },
    headerTitle: {
      fontSize: '22px',
      fontWeight: '700',
      flex: 1
    },
    filterBar: {
      backgroundColor: 'white',
      padding: '12px',
      display: 'flex',
      gap: '10px',
      borderRadius: '12px',
      marginBottom: '16px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
    },
    filterButton: (isActive: boolean) => ({
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      backgroundColor: isActive ? '#ea580c' : '#f3f4f6',
      color: isActive ? 'white' : '#374151',
      transition: 'all 0.2s',
      flex: 1
    }),
    issueCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '18px',
      marginBottom: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      borderLeft: '4px solid #ea580c'
    },
    severityBadge: (severity: string) => ({
      display: 'inline-block',
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '700',
      backgroundColor: severity === 'critical' ? '#dc2626' : severity === 'moderate' ? '#f59e0b' : '#22c55e',
      color: 'white'
    }),
    equipmentName: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px'
    },
    issueDescription: {
      fontSize: '17px',
      color: '#374151',
      marginBottom: '12px',
      lineHeight: '1.5'
    },
    metaRow: {
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap' as const,
      fontSize: '14px',
      color: '#64748b'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    resolveButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      padding: '12px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '14px'
    },
    resolvedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 14px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      backgroundColor: '#dcfce7',
      color: '#166534',
      marginTop: '14px'
    },
    noIssues: {
      textAlign: 'center' as const,
      padding: '60px 24px',
      color: '#9ca3af'
    },
    modalOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px',
      paddingTop: '5vh',
      paddingBottom: '40vh',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch' as const
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '85vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const,
      flexShrink: 0
    },
    modalHeader: {
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '700'
    },
    modalBody: {
      padding: '20px',
      overflowY: 'auto' as const,
      flex: 1
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '15px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      fontSize: '17px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      boxSizing: 'border-box' as const
    },
    textarea: {
      width: '100%',
      padding: '14px 16px',
      fontSize: '17px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      minHeight: '120px',
      fontFamily: 'inherit',
      resize: 'vertical' as const,
      boxSizing: 'border-box' as const
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      padding: '16px 20px',
      paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb'
    },
    cancelButton: {
      flex: 1,
      padding: '14px',
      borderRadius: '10px',
      border: '2px solid #e5e7eb',
      backgroundColor: 'white',
      color: '#374151',
      fontSize: '17px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    submitButton: {
      flex: 1,
      padding: '14px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: '#22c55e',
      color: 'white',
      fontSize: '17px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    deleteButton: {
      flex: 1,
      padding: '14px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: '#dc2626',
      color: 'white',
      fontSize: '17px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    editButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '10px 16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    actionButtonRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '16px'
    },
    severityGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '10px'
    },
    severityOption: (isSelected: boolean, color: string) => ({
      padding: '14px 10px',
      borderRadius: '10px',
      border: isSelected ? `3px solid ${color}` : '2px solid #e5e7eb',
      backgroundColor: isSelected ? `${color}15` : 'white',
      cursor: 'pointer',
      textAlign: 'center' as const,
      fontWeight: '600',
      fontSize: '15px',
      color: isSelected ? color : '#64748b'
    }),
    photoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '10px'
    },
    photoThumb: {
      aspectRatio: '1',
      borderRadius: '10px',
      overflow: 'hidden',
      cursor: 'pointer',
      border: '2px solid #e5e7eb'
    },
    infoCard: {
      backgroundColor: '#f8fafc',
      borderRadius: '10px',
      padding: '16px',
      marginBottom: '16px'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '16px'
    },
    infoLabel: {
      color: '#64748b',
      fontWeight: '500'
    },
    infoValue: {
      color: '#1e293b',
      fontWeight: '600'
    },
    searchContainer: {
      position: 'relative' as const
    },
    dropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      maxHeight: '200px',
      overflowY: 'auto' as const,
      zIndex: 100,
      marginTop: '4px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    dropdownItem: {
      padding: '14px 16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '16px'
    }
  };

  useEffect(() => {
    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    loadIssues();
    loadEquipment();
    loadShopTodos();
  }, []);

  useEffect(() => {
    filterIssues();
  }, [issues, statusFilter]);

  const loadIssues = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/issues?status=all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIssues(data.data);
      }
    } catch (error) {
      console.error('Error loading issues:', error);
      setError('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const loadEquipment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllEquipment(data.data);
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  const loadShopTodos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kiosk-todo/open`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setShopTodos(data.data);
      }
    } catch (error) {
      // Section just stays hidden if the list can't load
      console.error('Error loading shop to-dos:', error);
    }
  };

  const handleTodoDone = async (todo: ShopTodo) => {
    if (!window.confirm(`${t.confirmMarkDone}\n\n${todo.title}`)) return;
    setMarkingDoneId(todo.todo_id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/kiosk-todo/${todo.todo_id}/done`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      if (response.ok) {
        setShopTodos(prev => prev.filter(i => i.todo_id !== todo.todo_id));
      }
    } catch (error) {
      console.error('Error marking to-do done:', error);
    } finally {
      setMarkingDoneId(null);
    }
  };

  const todoPriorityColor = (p: string) => (p === 'high' ? '#dc2626' : p === 'low' ? '#94a3b8' : '#2563eb');

  const filterIssues = () => {
    if (statusFilter === 'all') {
      setFilteredIssues(issues);
    } else if (statusFilter === 'open') {
      setFilteredIssues(issues.filter(i => !i.is_fixed));
    } else {
      setFilteredIssues(issues.filter(i => i.is_fixed));
    }
  };

  const handleResolveClick = (issue: EquipmentIssue) => {
    setSelectedIssue(issue);
    setFixDescription('');
    setCostToFix('');
    setDowntimeHours('');
    fixPhotoPreviews.forEach(url => URL.revokeObjectURL(url));
    setFixPhotoFiles([]);
    setFixPhotoPreviews([]);
    setShowResolveModal(true);
  };

  const handleAddFixPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const combined = [...fixPhotoFiles, ...files].slice(0, 5); // backend accepts max 5
    setFixPhotoFiles(combined);
    setFixPhotoPreviews(prev => {
      prev.forEach(url => URL.revokeObjectURL(url));
      return combined.map(f => URL.createObjectURL(f));
    });
    e.target.value = '';
  };

  const handleRemoveFixPhoto = (index: number) => {
    const files = fixPhotoFiles.filter((_, i) => i !== index);
    setFixPhotoFiles(files);
    setFixPhotoPreviews(prev => {
      prev.forEach(url => URL.revokeObjectURL(url));
      return files.map(f => URL.createObjectURL(f));
    });
  };

  const handleResolveSubmit = async () => {
    if (!selectedIssue) return;

    setIsSubmitting(true);
    try {
      // JSON when no photos (matches old backend), multipart when photos attached
      let response: Response;
      if (fixPhotoFiles.length > 0) {
        const formData = new FormData();
        if (fixDescription) formData.append('fix_description', fixDescription);
        if (costToFix) formData.append('cost_to_fix', costToFix);
        if (downtimeHours) formData.append('downtime_hours', downtimeHours);
        fixPhotoFiles.forEach(file => formData.append('photos', file));

        response = await fetch(`${API_BASE_URL}/api/equipment/issues/${selectedIssue.issue_id}/resolve`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          },
          body: formData
        });
      } else {
        response = await fetch(`${API_BASE_URL}/api/equipment/issues/${selectedIssue.issue_id}/resolve`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fix_description: fixDescription || null,
            cost_to_fix: costToFix ? parseFloat(costToFix) : null,
            downtime_hours: downtimeHours ? parseInt(downtimeHours) : null
          })
        });
      }

      if (response.ok) {
        fixPhotoPreviews.forEach(url => URL.revokeObjectURL(url));
        setFixPhotoFiles([]);
        setFixPhotoPreviews([]);
        setShowResolveModal(false);
        setShowDetailModal(false);
        loadIssues();
      }
    } catch (error) {
      console.error('Error resolving issue:', error);
      setError('Failed to resolve issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleViewDetails = (issue: EquipmentIssue) => {
    setDetailIssue(issue);
    setPartsStatus(issue.parts_status || 'none');
    setShowDetailModal(true);
  };

  const handleEditClick = (issue: EquipmentIssue) => {
    setDetailIssue(issue);
    setEditDescription(issue.issue_description);
    setEditSeverity(issue.severity);
    setEditEquipmentId(issue.equipment_id);
    setEditPartsStatus(issue.parts_status || 'none');
    setEquipmentSearch(`${issue.equipment_name} (#${issue.bradanna_equipment_number})`);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!detailIssue) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/issues/${detailIssue.issue_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          issue_description: editDescription,
          severity: editSeverity,
          equipment_id: editEquipmentId,
          parts_status: editPartsStatus
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        loadIssues();
      }
    } catch (error) {
      console.error('Error updating issue:', error);
      setError('Failed to update issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (issue: EquipmentIssue) => {
    setDeleteIssue(issue);
    setShowDetailModal(false);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteIssue) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/issues/${deleteIssue.issue_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        setShowDeleteModal(false);
        loadIssues();
      }
    } catch (error) {
      console.error('Error deleting issue:', error);
      setError('Failed to delete issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPartsStatusLabel = (status: string | null) => {
    if (!status || status === 'none') return t.partsNone;
    const labels: Record<string, string> = {
      need_parts: t.partsNeedParts,
      parts_ordered: t.partsOrdered,
      waiting_on_parts: t.partsWaiting,
      parts_received: t.partsReceived
    };
    return labels[status] || status;
  };

  const getPartsStatusColor = (status: string | null) => {
    if (!status || status === 'none') return '#9ca3af';
    const colors: Record<string, string> = {
      need_parts: '#f59e0b',
      parts_ordered: '#3b82f6',
      waiting_on_parts: '#a855f7',
      parts_received: '#22c55e'
    };
    return colors[status] || '#9ca3af';
  };

  const filteredEquipmentList = allEquipment.filter(eq =>
    eq.equipment_name.toLowerCase().includes(equipmentSearch.toLowerCase()) ||
    eq.bradanna_equipment_number.toLowerCase().includes(equipmentSearch.toLowerCase())
  );

  const getSelectedEquipment = () => {
    return allEquipment.find(eq => eq.equipment_id === editEquipmentId);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Wrench size={24} />
          <h1 style={styles.headerTitle}>{t.title}</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Loader2 size={40} color="#ea580c" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Wrench size={24} />
        <h1 style={styles.headerTitle}>{t.title}</h1>
        <div style={{ fontSize: '16px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '8px' }}>
          {filteredIssues.length}
        </div>
      </div>

      {/* Filter Bar */}
      <div style={styles.filterBar}>
        <button onClick={() => setStatusFilter('open')} style={styles.filterButton(statusFilter === 'open')}>
          {t.open}
        </button>
        <button onClick={() => setStatusFilter('fixed')} style={styles.filterButton(statusFilter === 'fixed')}>
          {t.resolved}
        </button>
        <button onClick={() => setStatusFilter('all')} style={styles.filterButton(statusFilter === 'all')}>
          {t.all}
        </button>
      </div>

      {/* Issues List */}
      {filteredIssues.length === 0 ? (
        <div style={styles.noIssues}>
          <AlertCircle size={60} style={{ margin: '0 auto 20px', opacity: 0.3 }} />
          <div style={{ fontSize: '18px' }}>{t.noIssues}</div>
        </div>
      ) : (
        filteredIssues.map((issue) => {
          const photos = issue.issue_photos ? JSON.parse(issue.issue_photos) : [];
          return (
            <div
              key={issue.issue_id}
              style={{
                ...styles.issueCard,
                borderLeftColor: issue.severity === 'critical' ? '#dc2626' : issue.severity === 'moderate' ? '#f59e0b' : '#22c55e'
              }}
              onClick={() => handleViewDetails(issue)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={styles.equipmentName}>
                  {issue.equipment_name}
                  <span style={{ color: '#94a3b8', fontWeight: '500', fontSize: '16px' }}> #{issue.bradanna_equipment_number}</span>
                </div>
                <div style={styles.severityBadge(issue.severity)}>
                  {t[issue.severity]}
                </div>
              </div>

              <div style={styles.issueDescription}>
                {issue.issue_description}
              </div>

              <div style={styles.metaRow}>
                <div style={styles.metaItem}>
                  <User size={16} />
                  <span>{issue.reporter_first_name} {issue.reporter_last_name}</span>
                </div>
                <div style={styles.metaItem}>
                  <Calendar size={16} />
                  <span>{formatDate(issue.reported_date)}</span>
                </div>
                {photos.length > 0 && (
                  <div style={{ ...styles.metaItem, color: '#3b82f6', fontWeight: '600' }}>
                    📷 {photos.length}
                  </div>
                )}
                {issue.parts_status && issue.parts_status !== 'none' && (
                  <div style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backgroundColor: `${getPartsStatusColor(issue.parts_status)}20`,
                    color: getPartsStatusColor(issue.parts_status)
                  }}>
                    📦 {getPartsStatusLabel(issue.parts_status)}
                  </div>
                )}
              </div>

              {issue.is_fixed && (
                <div style={styles.resolvedBadge}>
                  <Check size={16} />
                  {t.resolvedOn} {formatDate(issue.fixed_date!)}
                </div>
              )}

              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <ChevronRight size={20} color="#9ca3af" />
              </div>
            </div>
          );
        })
      )}

      {/* Shop To-Do (kiosk board) — general shop tasks, separate from the
          equipment issues above; not counted in any issue count/KPI */}
      {shopTodos.length > 0 && (
        <div style={{ marginTop: '28px' }}>
          <div style={{ ...styles.header, backgroundColor: '#475569' }}>
            <ClipboardList size={24} />
            <div style={{ flex: 1 }}>
              <h2 style={{ ...styles.headerTitle, fontSize: '20px', margin: 0 }}>{t.shopTodoTitle}</h2>
              <div style={{ fontSize: '13px', opacity: 0.85 }}>{t.shopTodoSubtitle}</div>
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '8px' }}>
              {shopTodos.length}
            </div>
          </div>

          {shopTodos.map((todo) => (
            <div
              key={todo.todo_id}
              style={{
                ...styles.issueCard,
                cursor: 'default',
                borderLeftColor: todoPriorityColor(todo.priority)
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', lineHeight: 1.3 }}>
                    {todo.title}
                  </div>
                  <div style={{ ...styles.metaRow, marginTop: '8px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '800',
                      letterSpacing: '0.3px',
                      padding: '2px 8px',
                      borderRadius: '5px',
                      backgroundColor: todoPriorityColor(todo.priority)
                    }}>
                      {todo.priority.toUpperCase()}
                    </span>
                    {todo.created_by && (
                      <div style={styles.metaItem}>
                        <User size={16} />
                        <span>{t.addedBy} {todo.created_by}</span>
                      </div>
                    )}
                    <div style={styles.metaItem}>
                      <Calendar size={16} />
                      <span>{formatDate(todo.created_at)}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleTodoDone(todo)}
                  disabled={markingDoneId === todo.todo_id}
                  style={{
                    ...styles.resolveButton,
                    marginTop: 0,
                    flexShrink: 0,
                    opacity: markingDoneId === todo.todo_id ? 0.6 : 1
                  }}
                >
                  <Check size={18} />
                  {t.markDone}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && detailIssue && (
        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>{detailIssue.equipment_name}</div>
                <div style={{ opacity: 0.9, fontSize: '14px' }}>#{detailIssue.bradanna_equipment_number}</div>
              </div>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} color="white" />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Severity Badge */}
              <div style={{ marginBottom: '16px' }}>
                <span style={styles.severityBadge(detailIssue.severity)}>
                  {t[detailIssue.severity]}
                </span>
                {detailIssue.is_fixed && (
                  <span style={{ ...styles.resolvedBadge, marginLeft: '10px', marginTop: 0 }}>
                    <Check size={14} /> {t.resolved}
                  </span>
                )}
              </div>

              {/* Description */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.description}</label>
                <div style={{ fontSize: '18px', lineHeight: '1.6', color: '#1e293b' }}>
                  {detailIssue.issue_description}
                </div>
              </div>

              {/* Photos */}
              {detailIssue.issue_photos && JSON.parse(detailIssue.issue_photos).length > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.photos} ({JSON.parse(detailIssue.issue_photos).length})</label>
                  <div style={styles.photoGrid}>
                    {JSON.parse(detailIssue.issue_photos).map((url: string, idx: number) => (
                      <div key={idx} style={styles.photoThumb} onClick={() => setSelectedPhoto(url)}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fix Photos (taken when the issue was resolved) */}
              {detailIssue.is_fixed && detailIssue.fix_photos && JSON.parse(detailIssue.fix_photos).length > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.fixPhotos} ({JSON.parse(detailIssue.fix_photos).length})</label>
                  <div style={styles.photoGrid}>
                    {JSON.parse(detailIssue.fix_photos).map((url: string, idx: number) => (
                      <div key={idx} style={{ ...styles.photoThumb, border: '2px solid #22c55e' }} onClick={() => setSelectedPhoto(url)}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Card */}
              <div style={styles.infoCard}>
                <div style={{ ...styles.infoRow, borderTop: 'none' }}>
                  <span style={styles.infoLabel}>{t.reportedBy}</span>
                  <span style={styles.infoValue}>{detailIssue.reporter_first_name} {detailIssue.reporter_last_name}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.reportedOn}</span>
                  <span style={styles.infoValue}>{formatDate(detailIssue.reported_date)}</span>
                </div>
                {detailIssue.job_name && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>{t.location}</span>
                    <span style={styles.infoValue}>{detailIssue.job_name}</span>
                  </div>
                )}
                <div style={{ ...styles.infoRow, borderBottom: 'none' }}>
                  <span style={styles.infoLabel}>{t.partsStatus}</span>
                  <span style={{ ...styles.infoValue, color: getPartsStatusColor(detailIssue.parts_status) }}>
                    {getPartsStatusLabel(detailIssue.parts_status)}
                  </span>
                </div>
                {detailIssue.is_fixed && (
                  <>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>{t.resolvedOn}</span>
                      <span style={styles.infoValue}>{formatDate(detailIssue.fixed_date!)}</span>
                    </div>
                    {detailIssue.fix_description && (
                      <div style={{ ...styles.infoRow, flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                        <span style={styles.infoLabel}>Fix Description</span>
                        <span style={styles.infoValue}>{detailIssue.fix_description}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Buttons */}
              {!detailIssue.is_fixed && (
                <div style={styles.actionButtonRow}>
                  <button onClick={() => handleEditClick(detailIssue)} style={styles.editButton}>
                    <Edit2 size={16} /> {t.editIssue}
                  </button>
                  <button onClick={() => handleDeleteClick(detailIssue)} style={{ ...styles.editButton, backgroundColor: '#dc2626' }}>
                    <Trash2 size={16} /> {t.deleteIssue}
                  </button>
                </div>
              )}
            </div>

            {/* Resolve Button */}
            {!detailIssue.is_fixed && (
              <div style={styles.buttonGroup}>
                <button onClick={() => handleResolveClick(detailIssue)} style={styles.submitButton}>
                  <Check size={18} style={{ marginRight: '8px' }} />
                  {t.markResolved}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && detailIssue && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, backgroundColor: '#3b82f6' }}>
              <div style={styles.modalTitle}>{t.editIssue}</div>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} color="white" />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Equipment Selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.changeMachine}</label>
                <div style={styles.searchContainer}>
                  <input
                    type="text"
                    value={equipmentSearch}
                    onChange={(e) => {
                      setEquipmentSearch(e.target.value);
                      setShowEquipmentDropdown(true);
                    }}
                    onFocus={() => setShowEquipmentDropdown(true)}
                    placeholder={t.searchEquipment}
                    style={styles.input}
                  />
                  {showEquipmentDropdown && filteredEquipmentList.length > 0 && (
                    <div style={styles.dropdown}>
                      {filteredEquipmentList.slice(0, 5).map((eq) => (
                        <div
                          key={eq.equipment_id}
                          onClick={() => {
                            setEditEquipmentId(eq.equipment_id);
                            setEquipmentSearch(`${eq.equipment_name} (#${eq.bradanna_equipment_number})`);
                            setShowEquipmentDropdown(false);
                          }}
                          style={{
                            ...styles.dropdownItem,
                            backgroundColor: editEquipmentId === eq.equipment_id ? '#f0f9ff' : 'white'
                          }}
                        >
                          <div style={{ fontWeight: '600' }}>{eq.equipment_name}</div>
                          <div style={{ fontSize: '14px', color: '#64748b' }}>#{eq.bradanna_equipment_number}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Severity */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.severity}</label>
                <div style={styles.severityGrid}>
                  <div
                    onClick={() => setEditSeverity('minor')}
                    style={styles.severityOption(editSeverity === 'minor', '#22c55e')}
                  >
                    🟢 {t.minor}
                  </div>
                  <div
                    onClick={() => setEditSeverity('moderate')}
                    style={styles.severityOption(editSeverity === 'moderate', '#f59e0b')}
                  >
                    🟡 {t.moderate}
                  </div>
                  <div
                    onClick={() => setEditSeverity('critical')}
                    style={styles.severityOption(editSeverity === 'critical', '#dc2626')}
                  >
                    🔴 {t.critical}
                  </div>
                </div>
              </div>

              {/* Parts Status */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.partsStatus}</label>
                <CustomSelect
                  value={editPartsStatus}
                  onChange={(val) => setEditPartsStatus(val)}
                  options={[
                    { value: 'none', label: t.partsNone },
                    { value: 'need_parts', label: t.partsNeedParts },
                    { value: 'parts_ordered', label: t.partsOrdered },
                    { value: 'waiting_on_parts', label: t.partsWaiting },
                    { value: 'parts_received', label: t.partsReceived }
                  ]}
                />
              </div>

              {/* Description */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.description}</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  style={styles.textarea}
                />
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button onClick={() => setShowEditModal(false)} style={styles.cancelButton}>
                {t.cancel}
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={isSubmitting}
                style={{ ...styles.submitButton, backgroundColor: '#3b82f6', opacity: isSubmitting ? 0.6 : 1 }}
              >
                {isSubmitting ? 'Saving...' : t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteIssue && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, backgroundColor: '#dc2626' }}>
              <div style={styles.modalTitle}>{t.deleteIssue}</div>
              <button onClick={() => setShowDeleteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} color="white" />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Trash2 size={48} color="#dc2626" style={{ marginBottom: '16px' }} />
                <p style={{ fontSize: '18px', color: '#1e293b', marginBottom: '8px' }}>{t.confirmDelete}</p>
                <p style={{ fontSize: '15px', color: '#64748b' }}>{t.deleteWarning}</p>
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '15px' }}>
                  <strong>{deleteIssue.equipment_name}</strong>
                  <br />
                  {deleteIssue.issue_description.substring(0, 100)}...
                </div>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button onClick={() => setShowDeleteModal(false)} style={styles.cancelButton}>
                {t.cancel}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isSubmitting}
                style={{ ...styles.deleteButton, opacity: isSubmitting ? 0.6 : 1 }}
              >
                {isSubmitting ? 'Deleting...' : t.deleteIssue}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && selectedIssue && (
        <div style={styles.modalOverlay} onClick={() => setShowResolveModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, backgroundColor: '#22c55e' }}>
              <div style={styles.modalTitle}>{t.resolveIssue}</div>
              <button onClick={() => setShowResolveModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} color="white" />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ marginBottom: '20px', padding: '14px', backgroundColor: '#f8fafc', borderRadius: '10px' }}>
                <strong style={{ fontSize: '16px' }}>{selectedIssue.equipment_name}</strong>
                <div style={{ fontSize: '15px', color: '#64748b', marginTop: '4px' }}>
                  {selectedIssue.issue_description}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.fixDescription} <span style={{ color: '#9ca3af', fontWeight: '400' }}>({t.optional})</span>
                </label>
                <textarea
                  value={fixDescription}
                  onChange={(e) => setFixDescription(e.target.value)}
                  style={styles.textarea}
                  placeholder="Describe what was fixed..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.costToFix}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={costToFix}
                    onChange={(e) => setCostToFix(e.target.value.replace(/[^0-9.]/g, ''))}
                    style={styles.input}
                    placeholder="0.00"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.downtimeHours}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={downtimeHours}
                    onChange={(e) => setDowntimeHours(e.target.value.replace(/[^0-9]/g, ''))}
                    style={styles.input}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Fix Photos */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.fixPhotos} <span style={{ color: '#9ca3af', fontWeight: '400' }}>({t.optional})</span>
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {fixPhotoPreviews.map((url, idx) => (
                    <div key={idx} style={{ position: 'relative', width: '72px', height: '72px' }}>
                      <img
                        src={url}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px', border: '2px solid #e2e8f0' }}
                      />
                      <button
                        onClick={() => handleRemoveFixPhoto(idx)}
                        style={{
                          position: 'absolute', top: '-8px', right: '-8px',
                          width: '24px', height: '24px', borderRadius: '50%',
                          backgroundColor: '#dc2626', border: '2px solid white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', padding: 0
                        }}
                      >
                        <X size={14} color="white" />
                      </button>
                    </div>
                  ))}
                  {fixPhotoFiles.length < 5 && (
                    <label style={{
                      width: '72px', height: '72px', borderRadius: '10px',
                      border: '2px dashed #22c55e', backgroundColor: '#f0fdf4',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', gap: '2px'
                    }}>
                      <Camera size={22} color="#22c55e" />
                      <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: '600' }}>{t.addPhotos}</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAddFixPhotos}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button onClick={() => setShowResolveModal(false)} style={styles.cancelButton}>
                {t.cancel}
              </button>
              <button
                onClick={handleResolveSubmit}
                disabled={isSubmitting}
                style={{ ...styles.submitButton, opacity: isSubmitting ? 0.6 : 1 }}
              >
                {isSubmitting ? 'Submitting...' : t.submit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Fullscreen */}
      {selectedPhoto && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={28} color="white" />
          </button>
          <img
            src={selectedPhoto}
            alt="Full size"
            style={{ maxWidth: '95%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }}
          />
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
